import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, UserPlus, Mail, Lock, User } from 'lucide-react';
import { TridentIcon } from '../common/TridentIcon';
import { setToken } from '../../utils/authFetch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const endpoint = isRegister ? '/api/auth/register' : '/api/auth/login';
    const body = isRegister
      ? { email, password, name }
      : { email, password };

    try {
      const res = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.detail || 'Something went wrong');
        return;
      }

      setToken(data.token);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <div className="max-w-md w-full mx-auto rounded-2xl p-8 md:p-10 border border-border bg-card">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-foreground/5 border border-border flex items-center justify-center">
              <TridentIcon size={28} className="text-foreground" strokeWidth={2.5} />
            </div>
          </div>
          <h1 className="text-2xl font-bold tracking-tight brand mb-2" data-testid="auth-title">
            Trinity
          </h1>
          <p className="text-sm text-muted-foreground">
            {isRegister ? 'Create your account' : 'Sign in to your account'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {isRegister && (
            <div className="relative">
              <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required={isRegister}
                className="w-full h-11 pl-10 pr-4 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20"
                data-testid="register-name-input"
              />
            </div>
          )}

          <div className="relative">
            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full h-11 pl-10 pr-4 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20"
              data-testid="login-email-input"
            />
          </div>

          <div className="relative">
            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="w-full h-11 pl-10 pr-4 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20"
              data-testid="login-password-input"
            />
          </div>

          {error && (
            <p className="text-sm text-red-500 text-center" data-testid="auth-error">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full h-12 flex items-center justify-center gap-3 rounded-xl bg-foreground text-background font-medium hover:bg-foreground/90 active:scale-[0.985] focus-visible:ring-2 focus-visible:ring-foreground/50 transition-all duration-150 disabled:opacity-50"
            data-testid="login-submit-button"
          >
            {loading ? (
              <span className="animate-pulse">Please wait...</span>
            ) : isRegister ? (
              <><UserPlus size={20} /> Create Account</>
            ) : (
              <><LogIn size={20} /> Sign In</>
            )}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-muted-foreground">
          {isRegister ? (
            <p>Already have an account?{' '}
              <button onClick={() => { setIsRegister(false); setError(''); }} className="text-foreground font-medium hover:underline" data-testid="switch-to-login">
                Sign in
              </button>
            </p>
          ) : (
            <p>Don't have an account?{' '}
              <button onClick={() => { setIsRegister(true); setError(''); }} className="text-foreground font-medium hover:underline" data-testid="switch-to-register">
                Create one
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
